import type { DraftPreviewMode } from "./draftPreviewMode";
import type { FxQualityTier } from "./fxRuntime";

export type FxVideoOverlayBudgetMode =
  | "full"
  | "reduced"
  | "emergency";

export type ResolveFxVideoOverlayBudgetArgs = {
  isPlaying: boolean;
  draftPreviewMode: DraftPreviewMode;
  qualityTier: FxQualityTier;
  frameMs: number;
  activeSegmentationTargets: number;
};

export type FxVideoOverlayBudget = {
  maxRenderedLayers: number | null;
  /** Maximum number of elements that receive GPU shader overlay re-renders this frame. null = unlimited. */
  maxGpuOverlayTargets: number | null;
  /**
   * Minimum milliseconds between GPU overlay re-renders per element.
   * 0 = re-render every frame; 16 = ~30fps; 33 = ~20fps.
   * Infrastructure: entry.lastRenderMs is already tracked per element — this value gates re-use.
   */
  gpuOverlayThrottleMs: number;
  mode: FxVideoOverlayBudgetMode;
};

const EMERGENCY_FRAME_MS = 42;
const EMERGENCY_SEGMENTED_FRAME_MS = 34;
const REDUCED_FRAME_MS = 30;

function clampFrameMs(value: number): number {
  if (!Number.isFinite(value)) return 16.7;
  return Math.max(4, Math.min(120, value));
}

export function resolveFxVideoOverlayBudget(
  args: ResolveFxVideoOverlayBudgetArgs
): FxVideoOverlayBudget {
  const { isPlaying, draftPreviewMode, qualityTier, activeSegmentationTargets } = args;
  if (!isPlaying) {
    // Scrub/paused: unlimited, no throttle — we want the freshest frame immediately
    return { maxRenderedLayers: null, maxGpuOverlayTargets: null, gpuOverlayThrottleMs: 0, mode: "full" };
  }

  const frameMs = clampFrameMs(args.frameMs);
  const emergency =
    frameMs >= EMERGENCY_FRAME_MS ||
    (activeSegmentationTargets > 0 && frameMs >= EMERGENCY_SEGMENTED_FRAME_MS);
  if (emergency) {
    return {
      maxRenderedLayers: draftPreviewMode === "off" ? 10 : 8,
      // Emergency: cap GPU overlay re-renders at 5 elements/frame, throttle to ~20fps per element
      maxGpuOverlayTargets: 5,
      gpuOverlayThrottleMs: 33,
      mode: "emergency",
    };
  }

  if (
    draftPreviewMode === "on" ||
    (qualityTier === "draft" && (frameMs >= REDUCED_FRAME_MS || activeSegmentationTargets >= 3))
  ) {
    return {
      maxRenderedLayers: 18,
      // Reduced: cap at 12 elements/frame, throttle to ~30fps per element
      maxGpuOverlayTargets: 12,
      gpuOverlayThrottleMs: 16,
      mode: "reduced",
    };
  }

  return { maxRenderedLayers: null, maxGpuOverlayTargets: null, gpuOverlayThrottleMs: 0, mode: "full" };
}
